import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Typography,
  Button,
  Box,
  IconButton,
  TextField,
  Grid,
  Divider,
} from "@mui/material";
import { Add, Remove, AccessTime } from "@mui/icons-material";
import { useLogger } from "../../../contexts/LoggerContext";
import {
  KORPlayLog,
  KORPlaySelection,
  OffensivePlayLog,
  OffensivePlaySelection,
} from "../../../types/LogTypes";
import { useSchemes } from "../../../contexts/SchemesContext";
import { MultiSectionDigitalClock } from "@mui/x-date-pickers/MultiSectionDigitalClock";
import dayjs, { Dayjs } from "dayjs";
import {
  mmssToDayjs,
  dayjsToMMSS,
  MAX_TIME,
  MIN_TIME,
  timeToSeconds,
  secondsToTime,
} from "./Helpers/helpers";
import Slider from "@mui/material/Slider";

// TODO:
// - Fix UI/Ux for play call and play log
// - Fix UI/Ux for time adjustment

interface SavePlayProps {
  open: boolean;
  onClose: () => void;
  setClearSelections: (clear: boolean) => void;
}

export const SavePlayDialog = (props: SavePlayProps) => {
  const { open, onClose, setClearSelections } = props;
  const { onSaveCurrentPlay, setCurrentPlayLog, setCurrentPlayCall } =
    useLogger();

  const handleSaveCurrentPlay = () => {
    onSaveCurrentPlay();
    setCurrentPlayLog(null);
    setCurrentPlayCall(null);
    setClearSelections(true);
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Save Play Log</DialogTitle>
      <DialogContent>
        <Box sx={{ mb: 3 }}>
          <PlayLog />
        </Box>

        <Box sx={{ mb: 3 }}>
          <PlayCall />
        </Box>

        <Divider sx={{ my: 2 }} />

        <Box sx={{ mb: 3 }}>
          <AdjustTimeRemaining />
        </Box>

        <Box sx={{ display: "flex", gap: 2, justifyContent: "flex-end" }}>
          <Button onClick={onClose} color="secondary">
            Cancel
          </Button>
          <Button
            onClick={handleSaveCurrentPlay}
            color="primary"
            variant="contained"
          >
            Save
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

const PlayLog = () => {
  const { currentPlayLog, selectedSide } = useLogger();
  if (!currentPlayLog) {
    return null;
  }
  let stringLog = "";
  if (selectedSide === "OFFENSE") {
    const playLog = currentPlayLog as OffensivePlayLog;
    stringLog = `${playLog?.gain_loss} ${playLog?.passer?.name} ${playLog?.receiver?.name} ${playLog?.rusher?.name} ${playLog?.fumble?.fumble_player?.name} ${playLog?.interception?.interception_player?.name}`;
  }

  return (
    <Box>
      <Typography variant="h6">Play Log</Typography>
      <Typography variant="body1">
        {stringLog}
      </Typography>
    </Box>
  );
};

const PlayCall = () => {
  const { currentPlayCall, selectedSide } = useLogger();
  const {
    offensivePersonnel,
    formations,
    offensivePlayTags,
    runConcepts,
    formationTags,
  } = useSchemes();
  if (!currentPlayCall) {
    return null;
  }

  let personnelName: string | undefined = "";
  let formationName: string | undefined = "";
  let formationDirection: string | undefined = "";
  let runConceptName: string | undefined = "";
  let playTagName: string | undefined = "";
  let formationTagNames: string[] = [];

  if (selectedSide === "OFFENSE") {
    const playCall = currentPlayCall as OffensivePlaySelection;
    personnelName = offensivePersonnel.find(
      (p) => p.id === playCall.personnel_group_id
    )?.name;
    formationName = formations.find(
      (f) => f.id === playCall.formation_id
    )?.name;
    formationDirection = playCall.direction;
    runConceptName = runConcepts.find(
      (r) => r.id === playCall.run_concept_id
    )?.name;
    playTagName = offensivePlayTags.find(
      (t) => t.id === playCall.play_tag_id
    )?.name;
    formationTagNames = formationTags
      .filter((t) =>
        (playCall.formation_tags as number[]).includes(t.id as number)
      )
      .map((t) => t.name);
  }

  return (
    <Box>
      <Typography variant="h6">Play Call</Typography>
      <Typography variant="body1">
        {`(${personnelName}) ${formationName} ${formationDirection} ${runConceptName}`}
      </Typography>
    </Box>
  );
};

const AdjustTimeRemaining = () => {
  const { gameSituation, setGameSituation } = useLogger();

  console.log('gameSituation', gameSituation);

  const [timeInput, setTimeInput] = useState(gameSituation.time_remaining);
  const [clockValue, setClockValue] = useState<Dayjs>(
    mmssToDayjs(gameSituation.time_remaining)
  );

  // Update local state if gameSituation changes externally
  React.useEffect(() => {
    setTimeInput(gameSituation.time_remaining);
    setClockValue(mmssToDayjs(gameSituation.time_remaining));
  }, [gameSituation.time_remaining]);

  const handleClockChange = (value: Dayjs | null) => {
    if (value) {
      const clamped = value.isAfter(MAX_TIME)
        ? MAX_TIME
        : value.isBefore(MIN_TIME)
        ? MIN_TIME
        : value;
      setClockValue(clamped);
      setTimeInput(dayjsToMMSS(clamped));
      setGameSituation({
        ...gameSituation,
        time_remaining: dayjsToMMSS(clamped),
      });
    }
  };



  // Add quick subtract buttons
  const handleQuickSubtract = (secondsToSubtract: number) => {
    const [mm, ss] = timeInput.split(":").map(Number);
    let total = mm * 60 + ss - secondsToSubtract;
    total = Math.max(0, total);
    const newMM = Math.floor(total / 60);
    const newSS = total % 60;
    const newTime = `${newMM.toString().padStart(2, "0")}:${newSS
      .toString()
      .padStart(2, "0")}`;
    setTimeInput(newTime);
    setClockValue(mmssToDayjs(newTime));
    setGameSituation({
      ...gameSituation,
      time_remaining: newTime,
    });
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        width: "100%",
        p: 2,
      }}
    >
      <Typography variant="h6" gutterBottom>
        Game Clock
      </Typography>
      <Box sx={{ width: "100%", maxWidth: 400, mb: 2, display: 'flex', justifyContent: 'center' }}>
        <MultiSectionDigitalClock
          value={clockValue}
          onChange={handleClockChange}
          ampm={false}
          timeSteps={{ minutes: 1, seconds: 1 }}
          views={["minutes", "seconds"]}
          minTime={MIN_TIME}
          maxTime={MAX_TIME}
          skipDisabled
          sx={{
            '& .MuiMultiSectionDigitalClock-root': {
              fontSize: '2.5rem',
              background: 'transparent',
              width: '100%',
              minHeight: 400,
              display: 'flex',
              alignItems: 'center',
            },
            '& .Mui-selected': {
              background: '#1976d2',
              color: '#fff',
            },
          }}
        />
      </Box>
      <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
        <Button
          variant="outlined"
          size="large"
          onClick={() => handleQuickSubtract(15)}
        >
          -15s
        </Button>
        <Button
          variant="outlined"
          size="large"
          onClick={() => handleQuickSubtract(30)}
        >
          -30s
        </Button>
      </Box>
    </Box>
  );
};
